import { getDaysInMonth } from "@/utils/dayUtill";
import type { InboundInfo, InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import { inboundItemsFromInboundField } from "./inboundForView";
import { computeAssignmentStarts } from "./computeAssignmentStarts";
import type { AssignmentStartInfo } from "./types";

export type LockedSegmentReason = "outbound" | "outsider";

export interface LockedSegment extends AssignmentStartInfo {
  lockedReason: LockedSegmentReason;
}

export interface ClassifyInboundResult {
  /** 당월과 겹치는 항목 중 nurse가 타 병동으로 나가는 기간 items (target_group_id !== currentGroupId) */
  outboundRanges: InboundItem[];
  /** 당월과 겹치는 항목 중 우리 병동으로 오는 기간 items (target_group_id === currentGroupId) */
  inboundRanges: InboundItem[];
  /** 우리 병동에 파견 온 외부 인력 — inboundRanges > 0 && outboundRanges === 0 */
  isOutsider: boolean;
  /** 해당 월 기준으로 편집 불가인 1-based day 집합 */
  lockedDays: Set<number>;
  /** AssignmentGroupBar 시작 셀 렌더용 세그먼트 목록 */
  lockedSegments: LockedSegment[];
}

const EMPTY: ClassifyInboundResult = {
  outboundRanges: [],
  inboundRanges: [],
  isOutsider: false,
  lockedDays: new Set(),
  lockedSegments: [],
};

/** ISO "YYYY-MM-DD" → { y, m, d } 파싱 (실패 시 null) */
function parseYmd(iso: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

/** item의 당월 day 범위(1-based)를 반환. 겹치지 않으면 null */
function itemDayRange(
  item: InboundItem,
  year: number,
  month: number,
  daysInMonth: number
): { startDay: number; endDay: number } | null {
  const s = parseYmd(item.startDate);
  if (!s) return null;
  const e = item.endDate ? parseYmd(item.endDate) : null;
  if (item.endDate && !e) return null;

  const viewFirst = { y: year, m: month, d: 1 };
  const viewLast = { y: year, m: month, d: daysInMonth };

  function cmp(a: { y: number; m: number; d: number }, b: { y: number; m: number; d: number }) {
    if (a.y !== b.y) return a.y - b.y;
    if (a.m !== b.m) return a.m - b.m;
    return a.d - b.d;
  }

  if (cmp(s, viewLast) > 0) return null;
  if (e && cmp(e, viewFirst) < 0) return null;

  const startDay = cmp(s, viewFirst) < 0
    ? 1
    : s.y === year && s.m === month
      ? Math.min(Math.max(s.d, 1), daysInMonth)
      : 1;
  const endDay = !e || cmp(e, viewLast) >= 0
    ? daysInMonth
    : e.y === year && e.m === month
      ? Math.min(Math.max(e.d, 1), daysInMonth)
      : daysInMonth;

  if (startDay > endDay) return null;
  return { startDay, endDay };
}

/**
 * 연속 day 집합을 세그먼트 배열로 압축.
 * sortedDays: 오름차순 정렬된 1-based day 목록
 */
function daysToSegments(
  sortedDays: number[],
  reason: LockedSegmentReason,
  daysInMonth: number,
  targetGroupName?: string
): LockedSegment[] {
  if (sortedDays.length === 0) return [];
  const segments: LockedSegment[] = [];
  let segStart = sortedDays[0];
  let prev = sortedDays[0];

  for (let i = 1; i <= sortedDays.length; i++) {
    const cur = sortedDays[i];
    if (cur !== prev + 1) {
      const rangeLength = prev - segStart + 1;
      segments.push({
        reason,
        lockedReason: reason,
        startDay: segStart,
        rangeLength,
        targetGroupName,
        clippedStart: segStart === 1,
        clippedEnd: prev === daysInMonth,
      });
      if (cur !== undefined) {
        segStart = cur;
      }
    }
    prev = cur ?? prev;
  }
  return segments;
}

/**
 * `nurse.inbound`를 현재 병동 group_id 기준으로 분류해
 * lockedDays·lockedSegments를 반환한다.
 *
 * - `currentGroupId`가 falsy면 기능 비활성(기존 동작 유지).
 * - outbound: `target_group_id !== currentGroupId` → 해당 기간 locked.
 * - inbound(outsider): `target_group_id === currentGroupId` → 해당 기간 외 전부 locked.
 */
export function classifyInboundForGroup(
  inbound: InboundInfo | InboundItem[] | undefined,
  currentGroupId: string | null | undefined,
  daysInMonth: number,
  year: number,
  month: number
): ClassifyInboundResult {
  if (!currentGroupId) return EMPTY;

  const y = Math.trunc(Number(year));
  const mo = Math.trunc(Number(month));
  if (!Number.isFinite(y) || !Number.isFinite(mo) || mo < 1 || mo > 12 || y < 1) return EMPTY;
  const dim = getDaysInMonth(y, mo) || daysInMonth;

  const items = inboundItemsFromInboundField(inbound);
  if (items.length === 0) return EMPTY;

  const outboundRanges: InboundItem[] = [];
  const inboundRanges: InboundItem[] = [];

  for (const item of items) {
    // 분류·잠금 판단은 "현재 보고 있는 연/월과 실제로 겹치는 항목"만 기준으로 한다.
    // (예: 4월에 끝난 과거 파견 항목이 5월 outsider 판정을 깨지 않도록)
    const rangeInViewMonth = itemDayRange(item, y, mo, dim);
    if (!rangeInViewMonth) continue;
    const tid = item.target_group_id ?? item.targetGroupId ?? null;
    if (tid === currentGroupId) {
      inboundRanges.push(item);
    } else {
      outboundRanges.push(item);
    }
  }

  /**
   * 현재 병동(target=currentGroupId) 기간이 1일이라도 있으면
   * "외부 인력이 우리 병동에 머무는 창(window)"으로 해석한다.
   *
   * - inbound만 있는 경우: 기존 outsider 케이스
   * - inbound + outbound가 같이 있는 경우: 우리 병동에 머무는 기간 외(중간 공백 포함)도 잠금해야 함
   */
  const hasInboundWindow = inboundRanges.length > 0;
  const isOutsider = hasInboundWindow && outboundRanges.length === 0;

  const lockedDays = new Set<number>();
  const lockedSegments: LockedSegment[] = [];

  if (hasInboundWindow) {
    // outsider/window 공통: inbound 기간 외 전부 잠금
    const activeDays = new Set<number>();
    for (const item of inboundRanges) {
      const range = itemDayRange(item, y, mo, dim);
      if (!range) continue;
      for (let d = range.startDay; d <= range.endDay; d++) {
        activeDays.add(d);
      }
    }
    for (let d = 1; d <= dim; d++) {
      if (!activeDays.has(d)) {
        lockedDays.add(d);
      }
    }
    const sortedLocked = [...lockedDays].sort((a, b) => a - b);
    const outsiderSegs = daysToSegments(sortedLocked, "outsider", dim, undefined);
    lockedSegments.push(...outsiderSegs);
  } else if (outboundRanges.length > 0) {
    // outbound: 각 범위의 day들을 잠금
    for (const item of outboundRanges) {
      const range = itemDayRange(item, y, mo, dim);
      if (!range) continue;
      for (let d = range.startDay; d <= range.endDay; d++) {
        lockedDays.add(d);
      }
    }
    // lockedSegments: computeAssignmentStarts 재활용
    const starts = computeAssignmentStarts(outboundRanges, y, mo);
    for (const s of starts) {
      lockedSegments.push({ ...s, lockedReason: "outbound" });
    }
  }

  return { outboundRanges, inboundRanges, isOutsider, lockedDays, lockedSegments };
}
